import type { FC } from 'react';
import { useState } from 'react';
import { useUserStore } from '@Auth/store';
import Button from '@components/Button';
import InputMultiSelect from '@components/InputMultiSelect';
import InputSelect from '@components/InputSelect';
import InputText from '@components/InputText';
import Modal from '@components/Modal';
import { ApiPatchOperation } from '@domain/schemas/api-patch-operation';
import { IdentityProvider } from '@domain/schemas/identity-provider';
import {
  type CreateUserPayloadInput,
  createUserPayloadSchema,
  updateUserPayloadSchema,
} from '@domain/schemas/payload.schema';
import { UserRole } from '@domain/schemas/user-role';
import { isPasswordSignInEnabled } from '@helpers/auth/passwordSignIn';
import { selectAssignableTeams } from '@helpers/teams';
import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useAllTeams } from '@queries/fetchTeams';
import { useCreateUser, useUpdateUser } from '@queries/fetchUsers';
import type { CanonicalUserRecordWithCapabilities } from '@root/helpers/user-management/types';
import type { AnyZodObject } from 'zod';

/** The fields the API rejects a request without: every key its schema doesn't mark optional. */
const requiredFields = (schema: AnyZodObject) =>
  Object.keys(schema.shape).filter((key) => !schema.shape[key].isOptional());

const CREATE_REQUIRED_FIELDS = requiredFields(createUserPayloadSchema);
const UPDATE_REQUIRED_FIELDS = requiredFields(updateUserPayloadSchema.omit({ operation: true }));

const isSubmitDisabled = (requiredFields: string[], values: Record<string, any>) =>
  requiredFields.some((fieldName) => !values[fieldName]?.trim());

interface UserModalProps {
  handleClose: () => void;
  isAdd: boolean;
  user: CanonicalUserRecordWithCapabilities;
}

// The record types `role` as a string because Cognito does; the select only offers the roles the schema accepts.
type AssignableRole = CreateUserPayloadInput['role'];

const USER_ROLES: AssignableRole[] = [UserRole.Admin, UserRole.User, UserRole.Superadmin];

const UserModal: FC<UserModalProps> = ({ user, handleClose, isAdd }) => {
  const [formValues, setFormValues] = useState(user ?? {});
  const currentUser = useUserStore((s) => s.user);
  const refreshUser = useUserStore((s) => s.refreshUser);

  const getFeatureFlagState = useFeatureFlags();

  const { data: allTeams = [], isFetching: isFetchingTeams } = useAllTeams();

  const createUserMutation = useCreateUser();
  const updateUserMutation = useUpdateUser();
  const isLoading = createUserMutation.isPending || updateUserMutation.isPending;

  const isSSOEnabled = getFeatureFlagState('SSO');
  const externalIdentityProvider = import.meta.env.VITE_EXTERNAL_IDENTITY_PROVIDER;

  const identityProviders = [
    {
      id: IdentityProvider.Internal,
      name: 'Username & Password',
    },
    {
      id: IdentityProvider.External,
      name: 'Single Sign On',
    },
  ];

  // On add, Internal is only offered when password sign-in is enabled; on edit the field is read-only,
  // so an existing Internal user still needs its label even if the flag has since been disabled.
  const selectableIdentityProviders = isAdd
    ? identityProviders.filter((provider) => provider.id !== IdentityProvider.Internal || isPasswordSignInEnabled())
    : identityProviders;

  const selectedIdentityProvider =
    formValues.identityProvider ??
    (selectableIdentityProviders.length === 1 ? selectableIdentityProviders[0].id : undefined);

  const setField = <K extends keyof typeof formValues>(field: K, value: (typeof formValues)[K]) =>
    setFormValues((values) => ({ ...values, [field]: value }));

  const handleRoleChange = (role: AssignableRole) =>
    setFormValues((values) => {
      const isSuperadminChange = role === UserRole.Superadmin || values.role === UserRole.Superadmin;
      return { ...values, role, teams: isSuperadminChange ? [] : values.teams };
    });

  const handleSubmit = () => {
    const { username, firstName, lastName, email, role, teams } = formValues;
    const fields = {
      firstName,
      lastName,
      email,
      role: role as AssignableRole,
      teams,
      identityProvider: selectedIdentityProvider,
    };

    if (isAdd) {
      createUserMutation.mutate(fields, { onSuccess: handleClose });
    } else {
      updateUserMutation.mutate(
        { username, payload: { operation: ApiPatchOperation.UserUpdate, ...fields } },
        {
          onSuccess: async (updatedUser) => {
            if (currentUser?.username === username) {
              await refreshUser(updatedUser.updated_at);
            }
            handleClose();
          },
        },
      );
    }
  };

  const skipToggleEnableDisableOnUser = !user?.capabilitiesManager?.canBeEnabledOrDisabled;

  const handleToggleEnabledStateOnUser = () => {
    const { enabled, username } = formValues;

    handleClose();
    updateUserMutation.mutate({
      username,
      payload: { operation: ApiPatchOperation.UserChangeState, enabled: !enabled },
    });
  };

  const isSuperadminSelected = formValues.role === UserRole.Superadmin;

  return (
    <Modal
      open
      className="flex w-full flex-col gap-y-2 sm:w-5/6 sm:max-w-full lg:w-1/2"
      onClose={handleClose}
      title={isAdd ? 'Create user' : 'Edit user'}
    >
      <InputText
        className="mt-2"
        labelText="First Name"
        isRequired
        value={formValues.firstName}
        onChange={(event) => setField('firstName', event.target.value)}
      />
      <InputText
        labelText="Last Name"
        isRequired
        value={formValues.lastName}
        onChange={(event) => setField('lastName', event.target.value)}
      />
      <InputText
        labelText="Email"
        isRequired
        value={formValues.email}
        readOnly={!isAdd}
        onChange={isAdd ? (event) => setField('email', event.target.value) : () => {}}
      />
      <InputSelect
        labelText="Role"
        isRequired
        shouldRenderInsidePortal
        options={USER_ROLES.map((i) => ({ name: i, value: i }))}
        onChange={(role) => handleRoleChange(role as AssignableRole)}
        value={formValues.role}
      />
      {allTeams.length > 0 && (
        <InputMultiSelect
          labelText="Teams"
          shouldRenderInsidePortal
          options={selectAssignableTeams(allTeams, formValues.teams).map((team) => ({
            name: team.name,
            value: team.id,
          }))}
          isDisabled={isFetchingTeams || isSuperadminSelected}
          onChange={(teams) => setField('teams', teams)}
          value={formValues.teams}
        />
      )}
      {isSSOEnabled && externalIdentityProvider && (
        <InputSelect
          labelText="Sign Up Method"
          shouldRenderInsidePortal
          isLoading={isFetchingTeams}
          isReadOnly={!isAdd}
          options={selectableIdentityProviders.map((i) => ({ name: i.name, value: i.id }))}
          onChange={
            isAdd ? (identityProvider) => setField('identityProvider', identityProvider as IdentityProvider) : () => {}
          }
          value={identityProviders.find((i) => i.id === selectedIdentityProvider)?.name ?? ''}
        />
      )}
      {isAdd ? (
        <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
          <button
            disabled={isSubmitDisabled(CREATE_REQUIRED_FIELDS, formValues) || isLoading}
            onClick={handleSubmit}
            type="button"
            className="bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 inline-flex h-[38px] w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-xs focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-50 sm:ml-3 sm:w-auto sm:text-sm"
          >
            Create
          </button>
          <button
            onClick={handleClose}
            type="button"
            className="border-info-300 text-info-700 hover:bg-info-50 focus:ring-primary-500 mt-3 inline-flex h-[38px] w-full justify-center rounded-md border bg-white px-4 py-2 text-base font-medium shadow-xs focus:ring-2 focus:ring-offset-2 focus:outline-hidden sm:mt-0 sm:w-auto sm:text-sm"
          >
            Cancel
          </button>
        </div>
      ) : (
        <div className="flex flex-row">
          <div className="w-1/2 basis-1/2">
            <div className="mt-5 sm:mt-4 sm:flex">
              <Button
                className="inline-flex h-[38px] w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-xs focus:ring-2 focus:ring-offset-2 focus:outline-hidden sm:w-auto sm:text-sm"
                isDisabled={isLoading || skipToggleEnableDisableOnUser}
                onClick={handleToggleEnabledStateOnUser}
                type="button"
                kind={formValues.enabled ? 'danger' : 'green'}
              >
                {formValues.enabled ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>
          <div className="w-1/2 basis-1/2">
            <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
              <Button
                className="focus:ring-primary-500 inline-flex h-[38px] w-full justify-center rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-xs focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:text-white sm:ml-3 sm:w-auto sm:text-sm"
                loading={isLoading}
                kind="primary"
                onClick={handleSubmit}
                type="button"
                isDisabled={isSubmitDisabled(UPDATE_REQUIRED_FIELDS, formValues)}
              >
                Submit
              </Button>
              <Button
                className="border-info-300 text-info-700 hover:bg-info-50 focus:ring-primary-500 mt-3 inline-flex h-[38px] w-full justify-center rounded-md border bg-white px-4 py-2 text-base font-medium shadow-xs focus:ring-2 focus:ring-offset-2 focus:outline-hidden sm:mt-0 sm:w-auto sm:text-sm"
                isDisabled={isLoading}
                onClick={handleClose}
                type="button"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default UserModal;
